/**
 * useS360Tier — Hook central de limites do tier Sprints360.
 *
 * Fonte-única da verdade para "quanto o cliente pode fazer" no S360.
 * Mock persiste em LocalStorage (`s360_tier_v1`) enquanto não existe backend real.
 *
 * Phase 11.12 (Preços vivos):
 *  - Limites derivados de `planosStore.getS360Limits(tier)` (LocalStorage
 *    `s360_planos_v1`, alterável em /admin/s360-planos), com fallback numérico
 *    para não quebrar quando o admin não tiver customizado.
 *
 * Retorna:
 *   • tier: "START" | "SCALE" | "FULL" | "CONSULTING"
 *   • setTier(t): altera o tier ativo (protótipo). Se plano Consultoria estiver
 *                 ativo, é FORÇADO para CONSULTING.
 *   • limits: { projetos_max, usuarios_max, creditos_val_max, atividades_max, permite_consultoria }
 *   • usedProjetos, remainingProjetos, pctUso
 *   • podeCriarProjeto (bool) — o botão "Novo Projeto" deve respeitar
 *   • motivoBloqueio (string) — mensagem pronta para toast/upsell
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSprints360 } from "@/context/Sprints360Context";
import { useS360Consult } from "@/context/S360ConsultContext";
import { useTenant } from "@/context/TenantContext";
import { getS360Limits, S360_TIER_LABELS as LABELS } from "@/lib/planosStore";

const LS_KEY = "s360_tier_v1";
const DEFAULT_TIER = "FULL";

export const S360_TIER_LABELS = LABELS;

function readInitial() {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    if (raw && LABELS[raw]) return raw;
  } catch { /* noop */ }
  return DEFAULT_TIER;
}

export function useS360Tier() {
  const [tierStored, setTierStored] = useState(readInitial);
  const { state } = useSprints360();
  const { hasConsultoria, empresaAtiva: empresaConsultAtiva, empresas: empresasConsult } = useS360Consult();
  const { planoCodigo } = useTenant();

  // Phase 11.12 — bump só recalcula quando o admin salva no CRUD (evento
  // custom "planos:updated"). Também recalcula em mudanças de storage
  // originadas em outras abas.
  const [planosBump, setPlanosBump] = useState(0);
  useEffect(() => {
    const bump = () => setPlanosBump(x => x + 1);
    const onStorage = (e) => { if (e.key === "s360_planos_v1") bump(); };
    window.addEventListener("planos:updated", bump);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("planos:updated", bump);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, tierStored); } catch { /* noop */ }
  }, [tierStored]);

  // Phase 12.29c — Deriva o tier PREFERENCIALMENTE do `plano_codigo` do
  // backend (fonte-única). LS `s360_tier_v1` fica como fallback quando o
  // workspace ainda não tem S360 contratado.
  const tierDoPlano = useMemo(() => {
    const partes = String(planoCodigo || "").toUpperCase().split("+").map(s => s.trim());
    for (const p of partes) {
      if (p.startsWith("S360-")) {
        const t = p.slice(5);
        if (LABELS[t]) return t;
      }
    }
    return null;
  }, [planoCodigo]);

  const tierEffetivo = hasConsultoria ? "CONSULTING" : (tierDoPlano || tierStored);
  const limits = useMemo(() => getS360Limits(tierEffetivo), [tierEffetivo, planosBump]);

  const setTier = useCallback((t) => {
    if (!LABELS[t]) return;
    setTierStored(t);
  }, []);

  const usedProjetos = useMemo(() => {
    // Phase 12.71 — Arquivados NÃO contam para a quota ("N projetos ativos
    // atingidos"). Antes o count incluía arquivados, dando a impressão de
    // que arquivar "não liberava vaga" no plano.
    const arr = (state.projetos || []).filter(p => p.status !== "arquivado");
    if (hasConsultoria) {
      if (!empresaConsultAtiva) return 0;
      return arr.filter(p => p.empresa_simulada_id === empresaConsultAtiva.id).length;
    }
    return arr.length;
  }, [state.projetos, hasConsultoria, empresaConsultAtiva]);

  const usedAtividades = useMemo(() => {
    const projs = state.projetos || [];
    const escopoProjetoIds = new Set(
      hasConsultoria
        ? projs.filter(p => empresaConsultAtiva && p.empresa_simulada_id === empresaConsultAtiva.id).map(p => p.id)
        : projs.map(p => p.id)
    );
    return (state.atividades || []).filter(a => escopoProjetoIds.has(a.projeto_id)).length;
  }, [state.projetos, state.atividades, hasConsultoria, empresaConsultAtiva]);

  const usedParticipantes = useMemo(() => {
    const projs = state.projetos || [];
    const escopoProjs = hasConsultoria
      ? projs.filter(p => empresaConsultAtiva && p.empresa_simulada_id === empresaConsultAtiva.id)
      : projs;
    const setPart = new Set();
    escopoProjs.forEach(p => {
      (Array.isArray(p.participantes) ? p.participantes : []).forEach(x => {
        if (x?.email) setPart.add(String(x.email).toLowerCase());
        else if (x?.id) setPart.add(String(x.id));
        else if (typeof x === "string") setPart.add(x.toLowerCase());
      });
    });
    return setPart.size;
  }, [state.projetos, hasConsultoria, empresaConsultAtiva]);

  const remainingProjetos = Math.max(0, limits.projetos_max - usedProjetos);
  const pctUso = limits.projetos_max ? Math.round((usedProjetos / limits.projetos_max) * 100) : 0;
  const podeCriarProjeto = usedProjetos < limits.projetos_max;

  const remainingAtividades = Math.max(0, limits.atividades_max - usedAtividades);
  const podeCriarAtividade  = usedAtividades < limits.atividades_max;
  const remainingUsuarios   = Math.max(0, limits.usuarios_max - usedParticipantes);
  const podeAdicionarParticipante = usedParticipantes < limits.usuarios_max;

  const motivoBloqueio = podeCriarProjeto
    ? null
    : `Limite de ${limits.projetos_max} projeto${limits.projetos_max === 1 ? "" : "s"} ativos atingido no plano ${LABELS[tierEffetivo]}.`;
  const motivoBloqueioAtividade = podeCriarAtividade
    ? null
    : `Limite de ${limits.atividades_max} atividades atingido no plano ${LABELS[tierEffetivo]}.`;
  const motivoBloqueioParticipante = podeAdicionarParticipante
    ? null
    : `Limite de ${limits.usuarios_max} usuários atingido no plano ${LABELS[tierEffetivo]}.`;

  const usedEmpresasConsult = empresasConsult?.length || 0;

  return {
    tier: tierEffetivo,
    tierArmazenado: tierStored,
    tierBloqueado: hasConsultoria,
    setTier,
    limits,
    usedProjetos, remainingProjetos, pctUso, podeCriarProjeto, motivoBloqueio,
    usedAtividades, remainingAtividades, podeCriarAtividade, motivoBloqueioAtividade,
    usedParticipantes, remainingUsuarios, podeAdicionarParticipante, motivoBloqueioParticipante,
    usedEmpresasConsult,
  };
}
